/**
 * "Find the order" — the Orders search opened in place from an email that
 * has no linked order (kanban 0c4b9133, usage-review change 1).
 *
 * The person used to leave the inbox for /portal/orders and search there
 * (174 such moves in 90 days). This runs the same list search inside a
 * centered modal with the email's customer prefilled; picking a row hands
 * the order id back so the email view opens it beside the email.
 *
 * Recorded through portalTracking as `email_find_order` once per distinct
 * query, after the results are in, with the result count. The search term
 * is the only typed value recorded.
 */
import { useEffect, useRef, useState } from 'react';
import { Modal } from '../common/Modal';
import { portalOrderService } from '../../services/portalOrders';
import { trackEvent } from '../../services/portalTracking';
import { useDebouncedValue } from '../../hooks/useDebouncedValue';
import { formatShortDate } from '../../utils/dateFormatters';
import { ORDER_STATUS_LABELS, type OrderRecord } from '../../types/orders';
import styles from './FindOrderModal.module.css';

export interface FindOrderModalProps {
  emailId: string;
  /** Prefilled search term (customer number, or the sender's company). */
  initialTerm: string;
  /** The mapped customer number, or null for an unmapped email. */
  customerNo: string | null;
  onClose: () => void;
  onOpenOrder: (orderId: string) => void;
}

const RESULT_LIMIT = 25;

interface SearchResult {
  query: string;
  orders: OrderRecord[];
  total: number;
  error: string | null;
}

export default function FindOrderModal({
  emailId,
  initialTerm,
  customerNo,
  onClose,
  onOpenOrder,
}: FindOrderModalProps) {
  const [term, setTerm] = useState<string>(initialTerm);
  const debouncedTerm = useDebouncedValue(term, 300);
  const query = debouncedTerm.trim();
  // The latest completed search, keyed by its query so a stale result is
  // never shown for the current term and "loading" is derived, not stored.
  const [result, setResult] = useState<SearchResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  // Last query recorded, so a re-render never records the same query twice.
  const lastTrackedRef = useRef<string | null>(null);

  useEffect(() => {
    inputRef.current?.focus();
    inputRef.current?.select();
  }, []);

  useEffect(() => {
    if (!query) return;
    let cancelled = false;
    const record = (resultCount: number | null, error?: string) => {
      if (lastTrackedRef.current === query) return;
      lastTrackedRef.current = query;
      trackEvent('search', 'email_find_order', {
        email_id: emailId,
        customer_no: customerNo,
        query,
        result_count: resultCount,
        ...(error ? { error } : {}),
      });
    };
    portalOrderService
      .listOrders({ search: query, limit: RESULT_LIMIT })
      .then((response) => {
        if (cancelled) return;
        setResult({ query, orders: response.orders, total: response.total, error: null });
        record(response.total);
      })
      .catch(() => {
        if (cancelled) return;
        setResult({ query, orders: [], total: 0, error: 'Could not search orders. Try again.' });
        record(null, 'fetch_failed');
      });
    return () => {
      cancelled = true;
    };
  }, [query, emailId, customerNo]);

  const current = result && result.query === query ? result : null;
  const orders: OrderRecord[] = current?.orders ?? [];
  const total: number = current?.total ?? 0;
  const error: string | null = current?.error ?? null;
  const loading: boolean = !!query && current === null;

  return (
    <Modal isOpen onClose={onClose} title="Find the order" size="lg">
      <div className={styles.body} data-testid="find-order-modal">
        <label className={styles.searchLabel} htmlFor="find-order-search">
          {customerNo
            ? `Orders for customer ${customerNo}`
            : 'Search by customer, order number or PO'}
        </label>
        <input
          id="find-order-search"
          ref={inputRef}
          className={styles.searchInput}
          type="search"
          value={term}
          onChange={(e) => setTerm(e.target.value)}
          placeholder="Customer name, customer number, order or PO number"
          data-testid="find-order-search"
          autoComplete="off"
        />

        {error && (
          <div className={styles.error} role="alert">{error}</div>
        )}

        {!error && query && !loading && orders.length === 0 && (
          <div className={styles.empty} data-testid="find-order-empty">
            No orders match "{query}".
          </div>
        )}

        {!query && (
          <div className={styles.empty}>Type a customer, order or PO number.</div>
        )}

        {orders.length > 0 && (
          <div className={styles.results} data-testid="find-order-results">
            <div className={styles.resultsHead}>
              <span>
                {total > orders.length
                  ? `Showing ${orders.length} of ${total} orders`
                  : `${total} ${total === 1 ? 'order' : 'orders'}`}
              </span>
              {loading && <span className={styles.loading}>Searching…</span>}
            </div>
            <ul className={styles.list}>
              {orders.map((order) => (
                <li key={order.id}>
                  <button
                    type="button"
                    className={styles.row}
                    data-testid={`find-order-row-${order.id}`}
                    data-action="email_open_order"
                    data-target-email-id={emailId}
                    data-target-order-id={order.id}
                    data-outcome="open"
                    onClick={() => onOpenOrder(order.id)}
                    title="Open this order beside the email"
                  >
                    <span className={styles.rowMain}>
                      <span className={styles.customer}>{order.customer_name}</span>
                      <span className={styles.customerNo}>{order.customer_no}</span>
                    </span>
                    <span className={styles.rowMeta}>
                      <span className={styles.po}>
                        {order.po_number ? `PO ${order.po_number}` : 'No PO'}
                      </span>
                      {order.sage_order_no && (
                        <span className={styles.sageNo}>{order.sage_order_no}</span>
                      )}
                      <span className={styles.status}>
                        {ORDER_STATUS_LABELS[order.status] ?? order.status}
                      </span>
                      <span className={styles.date}>{formatShortDate(order.created_at)}</span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        {orders.length === 0 && loading && (
          <div className={styles.loading} data-testid="find-order-loading">Searching…</div>
        )}
      </div>
    </Modal>
  );
}
